import { useCallback, useEffect, useMemo, useRef } from 'react';
import { BlockerFunction } from 'react-router';

export interface INavigationBlockerParams {
  hasUnsavedChanges: boolean;
  onSave: () => Promise<unknown>;
  isSaving: boolean;
}

export function useUnsavedChangesNavigationBlocker({
  hasUnsavedChanges,
  onSave,
  isSaving,
}: INavigationBlockerParams) {
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  hasUnsavedChangesRef.current = hasUnsavedChanges;

  // Warn user about unsaved changes before browser unload (tab close/refresh)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChangesRef.current) {
        e.preventDefault();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) => {
      return currentLocation.pathname !== nextLocation.pathname && hasUnsavedChanges;
    },
    [hasUnsavedChanges]
  );

  const doBeforeProceed = useMemo(() => {
    return {
      onClick: async () => {
        await onSave();
      },
      label: 'Save and Exit',
      loading: isSaving,
    };
  }, [isSaving, onSave]);

  return useMemo(
    () => ({
      shouldBlock,
      doBeforeProceed,
    }),
    [shouldBlock, doBeforeProceed]
  );
}
